import React, { useState, useEffect } from 'react';
import { UserButton, useAuth } from '@clerk/clerk-react';
import { useUser } from '@clerk/clerk-react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import DashboardSidebar from '../components/DashboardSidebar';
import DashboardLanguageSwitcher from '../components/DashboardLanguageSwitcher';
import DashboardFinance from './DashboardFinance';
import DashboardOrders from './DashboardOrders';
import DashboardOrderDetail from './DashboardOrderDetail';
import DashboardOverview from './DashboardOverview';
import { withViewSupplier } from '../utils/dashboardViewSeller';
import './Dashboard.css';

function getApiBase() {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

export default function Dashboard() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { orderId } = useParams();
  const pathname = location.pathname || '';
  const isFinance = pathname.includes('/finance');
  const isOrders = pathname.includes('/orders');
  const isOrderDetail = Boolean(orderId);
  const [sellerInfo, setSellerInfo] = useState(null);
  const [sellerError, setSellerError] = useState(null);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [sellerLoading, setSellerLoading] = useState(true);
  const [sellerOptions, setSellerOptions] = useState([]);
  const [adminSelectNeeded, setAdminSelectNeeded] = useState(false);

  const isAdmin = user?.publicMetadata?.role === 'admin';
  const viewSupplier = (searchParams.get('viewSupplier') || '').trim();

  useEffect(() => {
    if (!isAdmin) {
      setSellerOptions([]);
      return;
    }
    let cancelled = false;
    async function loadOptions() {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const base = getApiBase();
        const res = await fetch(`${base}/api/dashboard/seller-options`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (cancelled) return;
        if (res.ok && Array.isArray(json.data?.options)) {
          setSellerOptions(json.data.options);
        }
      } catch (_) {
        if (!cancelled) setSellerOptions([]);
      }
    }
    loadOptions();
    return () => { cancelled = true; };
  }, [getToken, isAdmin]);

  useEffect(() => {
    let cancelled = false;
    setSellerLoading(true);
    setSellerError(null);
    async function fetchSellerData() {
      try {
        const token = await getToken();
        if (!token || cancelled) return;
        const base = getApiBase();
        const meUrl = withViewSupplier(`${base}/api/dashboard/me`, viewSupplier);
        const res = await fetch(meUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });
        let json;
        try {
          json = await res.json();
        } catch (_) {
          if (!cancelled) setSellerError(`Antwoord was geen JSON (status ${res.status}). Controleer of de API bereikbaar is.`);
          return;
        }
        if (cancelled) return;
        if (!res.ok) {
          if (json.code === 'ADMIN_SELECT_SELLER') {
            setAdminSelectNeeded(true);
            setSellerInfo(null);
            setBalanceSummary(null);
            setSellerError(null);
            return;
          }
          setAdminSelectNeeded(false);
          setSellerError(json.error || `Fout ${res.status}: Kon sellergegevens niet laden.`);
          return;
        }
        setAdminSelectNeeded(false);
        setSellerInfo(json.data);
        setSellerError(null);
        if (json.data?.supplierId) {
          const balUrl = withViewSupplier(`${base}/api/dashboard/balance`, viewSupplier);
          const balRes = await fetch(balUrl, {
            headers: { Authorization: `Bearer ${token}` },
          });
          const balJson = await balRes.json();
          if (!cancelled && balRes.ok && balJson.data?.items?.length) {
            const first = balJson.data.items[0];
            setBalanceSummary({
              total: first.balance_total,
              pending: first.balance_pending,
              available: first.balance_available,
            });
          } else if (!cancelled) {
            setBalanceSummary(null);
          }
        }
      } catch (e) {
        if (!cancelled) setSellerError(e.message || 'Fout bij ophalen gegevens (netwerk of CORS?).');
      } finally {
        if (!cancelled) setSellerLoading(false);
      }
    }
    fetchSellerData();
    return () => { cancelled = true; };
  }, [getToken, viewSupplier]);

  const onViewSellerChange = (nextId) => {
    if (nextId === '__pick__') return;
    const next = new URLSearchParams(searchParams);
    const v = (nextId || '').trim();
    if (v) next.set('viewSupplier', v);
    else next.delete('viewSupplier');
    setSearchParams(next, { replace: true });
  };

  const ownLinkedSeller = Boolean(sellerInfo?.ownLinkedSupplierId);
  const needsSellerPick = isAdmin && adminSelectNeeded && !viewSupplier;
  const sellerSelectValue = viewSupplier || (needsSellerPick ? '__pick__' : '');
  const showSellerDropdown =
    isAdmin && sellerOptions.length > 0 && (sellerInfo != null || adminSelectNeeded);

  return (
    <>
      <SEOHead
        title="Dashboard - SDeal"
        description="Your SDeal dashboard"
      />
      <div className="dashboard-page">
        <header className="admin-header-top">
          <Link to="/" className="admin-logo-link">
            <img src="/images/logo_sdeal_navbar.svg" alt="SDeal Logo" className="admin-logo" />
          </Link>
          <div className="dashboard-header-actions">
            {showSellerDropdown && (
              <label className="dashboard-view-seller">
                <span className="dashboard-view-seller-label">Seller</span>
                <select
                  className="dashboard-view-seller-select"
                  value={sellerSelectValue}
                  onChange={(e) => onViewSellerChange(e.target.value)}
                >
                  {needsSellerPick && (
                    <option value="__pick__" disabled>
                      Kies een seller…
                    </option>
                  )}
                  {ownLinkedSeller && (
                    <option value="">Mijn gekoppelde seller</option>
                  )}
                  {sellerOptions.map((opt) => (
                    <option key={opt.supplierId} value={opt.supplierId}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </label>
            )}
            <div className="dashboard-user">
              <DashboardLanguageSwitcher />
              <span className="dashboard-user-name">{user?.firstName || 'Gebruiker'}</span>
              <UserButton afterSignOutUrl="/" />
            </div>
          </div>
        </header>
        <div className="dashboard-body">
          <DashboardSidebar activeSection={isFinance ? 'finance' : isOrders ? 'orders' : 'dashboard'} />
          <main className="dashboard-main">
            {isAdmin && sellerInfo?.viewAsAdmin && (
              <p className="dashboard-admin-view-banner">
                Admin: je bekijkt het dashboard voor seller{' '}
                <strong>{sellerInfo.supplierId}</strong>
                {sellerInfo.displayName || sellerInfo.companyName
                  ? ` (${sellerInfo.displayName || sellerInfo.companyName})`
                  : ''}
                .
              </p>
            )}
            {isOrderDetail ? (
              <DashboardOrderDetail />
            ) : isFinance ? (
              <DashboardFinance />
            ) : isOrders ? (
              <DashboardOrders />
            ) : (
              <DashboardOverview
                sellerInfo={sellerInfo}
                balanceSummary={balanceSummary}
                sellerLoading={sellerLoading}
                sellerError={sellerError}
                adminSelectNeeded={adminSelectNeeded}
              />
            )}
          </main>
        </div>
      </div>
    </>
  );
}
